import { useCallback, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import type { InstoreEnrollmentStep } from 'src/utils/analytics/events';
import { tracker } from 'src/utils/analytics';
import { getLogger } from 'src/utils/datadog-logger';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { getAcceptedPaymentOptions } from 'shared/helpers/dispensaries';
import { useInstoreEnrollment } from 'src/hooks/use-instore-enrollment';

const LOG_NAMESPACE = 'instore-enrollment';

type LogFn = (message: string, context?: Record<string, unknown>) => void;

export type UseInstoreEnrollmentAnalyticsReturn = {
  // Step tracking
  trackStepViewed: (step: InstoreEnrollmentStep) => void;
  trackStepCompleted: (step: InstoreEnrollmentStep) => void;
  trackStepSkipped: (step: InstoreEnrollmentStep) => void;

  // Med card specific
  trackMedCardCompleted: (waitingAtDispensary: boolean) => void;
  trackMedCardFailed: (errorType: string, errorMessage: string) => void;

  // Loyalty specific
  trackLoyaltyCompleted: () => void;
  trackLoyaltySkipped: () => void;

  // Error logging (Datadog)
  logError: LogFn;
  logInfo: LogFn;
  logWarn: LogFn;
};

/**
 * Central analytics hook for the Instore Enrollment flow.
 * Handles step tracking, med card signup events, loyalty events,
 * and Datadog error logging with proper context.
 */
export const useInstoreEnrollmentAnalytics = (): UseInstoreEnrollmentAnalyticsReturn => {
  const { dispensary } = useDispensary();
  const isDutchiePayEnabledForDispo = !!getAcceptedPaymentOptions(dispensary)?.dutchiePay;
  const instoreEnrollment = useInstoreEnrollment();
  const enrollmentSessionId = useObserver(() => instoreEnrollment.enrollmentSessionId);
  const logger = useMemo(() => getLogger(LOG_NAMESPACE), []);

  // Step tracking - tracks when a step is viewed
  const trackStepViewed = useCallback(
    (step: InstoreEnrollmentStep): void => {
      tracker.setContext({ activeDispensary: { isDutchiePayEnabledForDispo, ...dispensary } });
      tracker.instoreEnrollmentStepViewed({ step });
    },
    [dispensary, isDutchiePayEnabledForDispo]
  );

  // Step tracking - tracks when a step is completed
  const trackStepCompleted = useCallback((step: InstoreEnrollmentStep): void => {
    tracker.instoreEnrollmentStepCompleted({ step });
  }, []);

  // Step tracking - tracks when a step is skipped
  const trackStepSkipped = useCallback((step: InstoreEnrollmentStep): void => {
    tracker.instoreEnrollmentStepSkipped({ step });
  }, []);

  // Med card tracking - successful submission
  const trackMedCardCompleted = useCallback(
    (waitingAtDispensary: boolean): void => {
      tracker.medCardSignupCompleted({ waitingAtDispensary });
      trackStepCompleted('medIntake');
    },
    [trackStepCompleted]
  );

  // Med card tracking - failed submission
  const trackMedCardFailed = useCallback(
    (errorType: string, errorMessage: string): void => {
      tracker.medCardSignupFailed({ errorType, errorMessage });

      // Also log to Datadog for alerting
      logger.error('Med card signup failed', {
        dispensaryId: dispensary?.id,
        enrollmentSessionId,
        errorType,
        errorMessage,
      });
    },
    [dispensary?.id, enrollmentSessionId, logger]
  );

  // Loyalty tracking - successful signup
  const trackLoyaltyCompleted = useCallback((): void => {
    tracker.loyaltySignup({
      analyticsEventLabel: 'instore-enrollment',
    });
    trackStepCompleted('loyalty');
  }, [trackStepCompleted]);

  // Loyalty tracking - user skipped
  const trackLoyaltySkipped = useCallback((): void => {
    trackStepSkipped('loyalty');
  }, [trackStepSkipped]);

  // Datadog logging - error level
  const logError = useCallback(
    (message: string, context?: Record<string, unknown>): void => {
      logger.error(message, {
        dispensaryId: dispensary?.id,
        enrollmentSessionId,
        ...context,
      });
    },
    [dispensary?.id, enrollmentSessionId, logger]
  );

  // Datadog logging - info level
  const logInfo = useCallback(
    (message: string, context?: Record<string, unknown>): void => {
      logger.info(message, {
        dispensaryId: dispensary?.id,
        enrollmentSessionId,
        ...context,
      });
    },
    [dispensary?.id, enrollmentSessionId, logger]
  );

  // Datadog logging - warn level
  const logWarn = useCallback(
    (message: string, context?: Record<string, unknown>): void => {
      logger.warn(message, {
        dispensaryId: dispensary?.id,
        enrollmentSessionId,
        ...context,
      });
    },
    [dispensary?.id, enrollmentSessionId, logger]
  );

  return {
    trackStepViewed,
    trackStepCompleted,
    trackStepSkipped,
    trackMedCardCompleted,
    trackMedCardFailed,
    trackLoyaltyCompleted,
    trackLoyaltySkipped,
    logError,
    logInfo,
    logWarn,
  };
};
